/**
 * Which timezone a table of timestamps is rendered in, and what to call it.
 *
 * ## The trap this exists to close
 *
 * The `timezone` preference is not always an IANA zone. Its default is the
 * sentinel `'exchange'`, meaning *each contract in its own exchange's zone* —
 * which is the neutral answer for a product with no default trading style.
 *
 * Passing that sentinel to `Intl` throws, and a `catch` that quietly falls back
 * to the browser's zone produces the exact failure a named column header is
 * supposed to prevent: a header reading `(exchange)` over timestamps rendered
 * in whatever zone the laptop happens to be in. Under that preference the zone
 * genuinely varies per row, so it has to be resolved per row.
 *
 * ## The label
 *
 * Under an explicit preference the header names it, because it is one zone and
 * naming it is unambiguous. Under `'exchange'` the header says so rather than
 * naming a zone it cannot promise — unless every contract on screen happens to
 * share one, in which case naming it is both true and more useful.
 */
import type { Instrument } from '@aegis/api-client';

export const EXCHANGE = 'exchange';

export interface ZoneResolver {
  /** The zone for one symbol, or undefined to leave it to the browser. */
  readonly of: (symbol: string) => string | undefined;
  /** What the column header should say. Null renders no zone at all. */
  readonly label: string | null;
}

/** Renders in the browser's zone and claims nothing. The honest fallback. */
export const NO_ZONE: ZoneResolver = { of: () => undefined, label: null };

export function zoneResolver(
  preference: string | null,
  instruments: readonly Instrument[],
  symbols: readonly string[] = [],
): ZoneResolver {
  if (!preference) return NO_ZONE;

  if (preference !== EXCHANGE) {
    // One zone, named. `fmtTime` falls back to the browser on an invalid zone,
    // so verify it before promising it in a header.
    return isValidZone(preference)
      ? { of: () => preference, label: preference }
      : NO_ZONE;
  }

  const byRoot = new Map<string, string>();
  for (const i of instruments) {
    byRoot.set(i.root, i.sessionTimezone);
    byRoot.set(i.canonicalSymbol, i.sessionTimezone);
  }
  const of = (symbol: string): string | undefined =>
    byRoot.get(symbol) ?? byRoot.get(rootOf(symbol));

  // If everything on screen shares a zone, name it — "America/Chicago" tells
  // the reader more than "exchange" does, and it is equally true.
  const seen = new Set(symbols.map(of).filter((z): z is string => z !== undefined));
  const missing = symbols.some((s) => of(s) === undefined);
  const label = !missing && seen.size === 1 ? [...seen][0]! : EXCHANGE;

  return { of, label: label ?? EXCHANGE };
}

/**
 * `MESU6` -> `MES`. Contract codes append a month letter and a year digit.
 *
 * Only used as a fallback after an exact match fails, so a symbol that is
 * already a root is never mangled.
 */
function rootOf(symbol: string): string {
  const m = /^([A-Z0-9]+?)[FGHJKMNQUVXZ]\d{1,2}$/.exec(symbol);
  return m?.[1] ?? symbol;
}

function isValidZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}
